import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import { useLocale } from "../i18n";

interface ConfirmOptions {
  confirmLabel?: string;
  danger?: boolean;
  message: string;
  title?: string;
}

interface PendingConfirmation extends ConfirmOptions {
  resolve: (confirmed: boolean) => void;
}

interface ConfirmAction {
  (options: ConfirmOptions | string): Promise<boolean>;
  run: (options: ConfirmOptions | string, action: () => void) => void;
}

const ConfirmContext = createContext<ConfirmAction | null>(null);

export function ConfirmDialogProvider({ children }: PropsWithChildren) {
  const { locale } = useLocale();
  const [pending, setPending] = useState<PendingConfirmation | null>(null);
  const pendingRef = useRef<PendingConfirmation | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();

  const settle = useCallback((confirmed: boolean) => {
    const current = pendingRef.current;
    if (!current) return;
    pendingRef.current = null;
    setPending(null);
    current.resolve(confirmed);
  }, []);

  const requestConfirmation = useCallback((options: ConfirmOptions | string) => {
    pendingRef.current?.resolve(false);
    return new Promise<boolean>((resolve) => {
      const normalized = typeof options === "string" ? { message: options } : options;
      const next = { ...normalized, resolve };
      pendingRef.current = next;
      setPending(next);
    });
  }, []);
  const confirmAction = useMemo<ConfirmAction>(() => Object.assign(
    requestConfirmation,
    {
      run(options: ConfirmOptions | string, action: () => void) {
        void requestConfirmation(options).then((confirmed) => {
          if (confirmed) action();
        });
      },
    },
  ), [requestConfirmation]);

  useEffect(() => {
    if (!pending) return;
    previousFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() => cancelRef.current?.focus());
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        settle(false);
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
        ),
      );
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocusRef.current?.focus();
    };
  }, [pending, settle]);

  useEffect(() => () => pendingRef.current?.resolve(false), []);

  const isLao = locale === "lo-LA";
  return (
    <ConfirmContext.Provider value={confirmAction}>
      {children}
      {pending ? (
        <div className="dialog-backdrop" onMouseDown={(event) => {
          if (event.target === event.currentTarget) settle(false);
        }}>
          <div
            aria-describedby={descriptionId}
            aria-labelledby={titleId}
            aria-modal="true"
            className="confirm-dialog"
            ref={dialogRef}
            role="alertdialog"
          >
            <h2 id={titleId}>{pending.title ?? (isLao ? "ຢືນຢັນການດຳເນີນການ" : "ยืนยันการดำเนินการ")}</h2>
            <p id={descriptionId}>{pending.message}</p>
            <div className="dialog-actions">
              <button className="button button-secondary" ref={cancelRef} type="button" onClick={() => settle(false)}>
                {isLao ? "ຍົກເລີກ" : "ยกเลิก"}
              </button>
              <button
                className={`button ${pending.danger ? "button-danger" : "button-primary"}`}
                type="button"
                onClick={() => settle(true)}
              >
                {pending.confirmLabel ?? (isLao ? "ຢືນຢັນ" : "ยืนยัน")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </ConfirmContext.Provider>
  );
}

export function useConfirmAction(): ConfirmAction {
  const confirmAction = useContext(ConfirmContext);
  if (!confirmAction) throw new Error("ConfirmDialogProvider is missing.");
  return confirmAction;
}
